import React, { useState, useEffect, useMemo, useRef, useContext } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { MapContainer, TileLayer, Marker, Popup, Polyline, ZoomControl, Circle, Polygon, Tooltip, useMap } from 'react-leaflet';
import L from 'leaflet';
import Axios from 'axios';
import { Box, Typography, Button, CircularProgress, Modal, Drawer, Alert, useMediaQuery, useTheme, IconButton, Chip, Menu, MenuItem, Tooltip as MuiTooltip, FormControlLabel, Checkbox } from '@mui/material';
import LayersIcon from '@mui/icons-material/Layers';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import DirectionsRunIcon from '@mui/icons-material/DirectionsRun';
import RunningTracker from './RunningTracker';
import Select from '@mui/material/Select';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import config, { environment } from '../../config';
import { getMarkIcon, getRouteColor, getPointsAlongPath, markIcons } from '../../Utils/RouteUtils';
import { IconsList } from '../../Utils/IconDisplay';
import StateContext from "../../Contexts/StateContext";
import SzekelyNepballadak from '../../Assets/SzekelyNepballadak.png';
import SzekelyNepdalok from '../../Assets/SzekelyNepdalok.png';
import Locsoloversek from '../../Assets/Locsoloversek.png';

const defaultIcon = new L.Icon({
    iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
});

const locationIcon = L.divIcon({
    html: renderToStaticMarkup(<MyLocationIcon sx={{ fontSize: '28px' }} color="primary" />),
    className: 'custom-location-icon',
    iconSize: [28, 28],
    iconAnchor: [14, 14],
});

const createThumbnailIcon = (url, category, isMobile = false) => {
    // Fallback if URL is missing
    let displayUrl = url;
    
    if (!displayUrl) {
        if (category === 'ballada') {
            displayUrl = SzekelyNepballadak;
        } else if (category === 'nepdal') {
            displayUrl = SzekelyNepdalok;
        } else if (category === 'locsoloversek') {
            displayUrl = Locsoloversek;
        } else {
            displayUrl = 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png';
        }
    }

    // Csángó speciális stílus (pl. bordó szegély)
    const isCsango = category === 'csango';
    const borderColor = isCsango ? '#8b0000' : '#ffffff';
    const borderWidth = isCsango ? '3px' : '2px';
    
    // Alapértelmezett méretek (panteon 1:1)
    let width = isMobile ? 60 : 80;
    let height = isMobile ? 60 : 80;
    let borderRadius = '50%'; // Panteon marad kör alakú

    // Kategória specifikus arányok
    if (category === 'nepviselet' || category === 'csango') {
        // 2:3 arány (magas téglalap)
        width = isMobile ? 44 : 60;
        height = isMobile ? 66 : 90;
        borderRadius = '4px';
    } else if (category === 'nepi_epiteszet' || category === 'szekelykapu') {
        // 5:4 arány
        width = isMobile ? 60 : 80;
        height = isMobile ? 48 : 64;
        borderRadius = '4px';
    } else if (category === 'futuristique') {
        // 4:3 arány
        width = isMobile ? 60 : 80;
        height = isMobile ? 45 : 60;
        borderRadius = '4px';
    } else if (category === 'panteon' || category === 'ballada' || category === 'nepdal' || category === 'locsoloversek') {
        // 1:1 arány (kör)
        width = isMobile ? 60 : 80;
        height = isMobile ? 60 : 80;
        borderRadius = '50%';
    } else {
        // Alapértelmezett más kategóriákra ha van kép
        width = isMobile ? 60 : 80;
        height = isMobile ? 60 : 80;
        borderRadius = '4px';
    }
    
    return L.divIcon({
        html: `<div class="thematic-thumbnail-marker" style="width: ${width}px; height: ${height}px; border-radius: ${borderRadius}; border: ${borderWidth} solid ${borderColor}; box-shadow: 0 0 8px rgba(0,0,0,0.4); overflow: hidden; background-color: #ffffff; display: flex; align-items: center; justify-content: center; z-index: 1000;">
                <img src="${displayUrl}" 
                     alt="thumb" 
                     loading="lazy"
                     decoding="async"
                     style="width: 100%; height: 100%; object-fit: cover; border-radius: ${borderRadius}; display: block; image-rendering: -webkit-optimize-contrast;" 
                     onerror="if (this.src !== 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png') { this.src='https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png'; this.style.border='2px solid blue'; }"
                />
               </div>`,
        className: 'custom-div-icon',
        iconSize: [width, height],
        iconAnchor: [width / 2, height / 2],
        popupAnchor: [0, -height / 2]
    });
};

const ZoomHandler = ({ setZoom, userLocation, setUserLocation, isFullscreen, isMobile, isLocating, isTracking }) => {
    const map = useMap();
    
    useEffect(() => {
        const onZoom = () => setZoom(map.getZoom());
        map.on('zoomend', onZoom);
        return () => map.off('zoomend', onZoom);
    }, [map, setZoom]);

    useEffect(() => {
        if (isLocating || isTracking) {
            map.locate({ 
                setView: false, 
                watch: true,
                enableHighAccuracy: true,
                maximumAge: 0,
                timeout: 5000
            });
            
            const onLocationFound = (e) => {
                const newPos = {
                    lat: e.latlng.lat,
                    lng: e.latlng.lng,
                    speed: e.speed,
                    altitude: e.altitude,
                    heading: e.heading,
                    accuracy: e.accuracy
                };
                setUserLocation(newPos);
                if (isTracking) {
                    map.panTo(e.latlng);
                }
            };
            
            const onLocationError = (e) => {
                console.error("Location error:", e.message);
            };

            map.on('locationfound', onLocationFound);
            map.on('locationerror', onLocationError);

            return () => {
                map.stopLocate();
                map.off('locationfound', onLocationFound);
                map.off('locationerror', onLocationError);
            };
        } else {
            setUserLocation(null);
        }
    }, [map, isLocating, isTracking, setUserLocation]);

    return null;
};

    const MapControls = ({ isMobile, userLocation, isLocating, setIsLocating, isFullscreen, setIsFullscreen, mapType, setMapType, showRunning, selectedInfo, setOpenTracker, showCsango, setShowCsango }) => {
    const map = useMap();
    const { t } = useTranslation();
    const [fsContainer, setFsContainer] = useState(null);
    const [locContainer, setLocContainer] = useState(null);
    const [layersContainer, setLayersContainer] = useState(null);
    const [trackerContainer, setTrackerContainer] = useState(null);
    const [csangoContainer, setCsangoContainer] = useState(null);

    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    const handleLayersClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const toggleCsango = () => {
        setShowCsango(!showCsango);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleMapTypeChange = (type) => {
        setMapType(type);
        handleClose();
    };

    useEffect(() => {
        const FullscreenControl = L.Control.extend({
            onAdd: function() {
                const container = L.DomUtil.create('div', 'leaflet-bar leaflet-control');
                const button = L.DomUtil.create('a', 'fullscreen-button', container);
                button.href = '#';
                button.title = 'Toggle Fullscreen';
                button.style.backgroundColor = 'white';
                button.style.width = '30px';
                button.style.height = '30px';
                button.style.display = 'flex';
                button.style.alignItems = 'center';
                button.style.justifyContent = 'center';
                button.style.cursor = 'pointer';

                L.DomEvent.disableClickPropagation(button);
                L.DomEvent.on(button, 'click', function(e) {
                    L.DomEvent.stop(e);
                    toggleFullscreen();
                });

                setFsContainer(button);
                return container;
            },
            onRemove: function() {
                setFsContainer(null);
            }
        });

        const LocationControl = L.Control.extend({
            onAdd: function() {
                const container = L.DomUtil.create('div', 'leaflet-bar leaflet-control');
                const button = L.DomUtil.create('a', 'location-button fullscreen-button', container);
                button.href = '#';
                button.title = 'My Location';
                button.style.backgroundColor = 'white';
                button.style.width = '30px';
                button.style.height = '30px';
                button.style.display = 'flex'; 
                button.style.alignItems = 'center';
                button.style.justifyContent = 'center';
                button.style.cursor = 'pointer';

                L.DomEvent.disableClickPropagation(button);
                L.DomEvent.on(button, 'click', function(e) {
                    L.DomEvent.stop(e);
                    if (!isLocating) {
                        setIsLocating(true);
                    } else if (userLocation) {
                        map.flyTo([userLocation.lat, userLocation.lng], map.getZoom());
                    }
                });

                setLocContainer(button);
                return container;
            },
            onRemove: function() {
                setLocContainer(null);
            }
        });

        const LayersControl = L.Control.extend({
            onAdd: function() {
                const container = L.DomUtil.create('div', 'leaflet-bar leaflet-control');
                const button = L.DomUtil.create('a', 'layers-button fullscreen-button', container);
                button.href = '#';
                button.title = t('Common.MapType', 'Térkép típusa');
                button.style.backgroundColor = 'white';
                button.style.width = '30px';
                button.style.height = '30px';
                button.style.display = 'flex';
                button.style.alignItems = 'center';
                button.style.justifyContent = 'center';
                button.style.cursor = 'pointer';

                L.DomEvent.disableClickPropagation(button);
                L.DomEvent.on(button, 'click', function(e) {
                    L.DomEvent.stop(e);
                    handleLayersClick(e);
                });

                setLayersContainer(button);
                return container;
            },
            onRemove: function() {
                setLayersContainer(null);
            }
        });

        const TrackerControl = L.Control.extend({
            onAdd: function() {
                const container = L.DomUtil.create('div', 'leaflet-bar leaflet-control');
                const button = L.DomUtil.create('a', 'tracker-button fullscreen-button', container);
                button.href = '#';
                button.title = t('Running.Tracker.Title', 'Futás követése');
                button.style.backgroundColor = 'white';
                button.style.width = '30px';
                button.style.height = '30px';
                button.style.display = 'flex';
                button.style.alignItems = 'center';
                button.style.justifyContent = 'center';
                button.style.cursor = 'pointer';

                L.DomEvent.disableClickPropagation(button);
                L.DomEvent.on(button, 'click', function(e) {
                    L.DomEvent.stop(e);
                    setOpenTracker(true);
                });

                setTrackerContainer(button);
                return container;
            },
            onRemove: function() {
                setTrackerContainer(null);
            }
        });

        const CsangoControl = L.Control.extend({
            onAdd: function() {
                const container = L.DomUtil.create('div', 'leaflet-bar leaflet-control');
                const button = L.DomUtil.create('a', 'csango-button fullscreen-button', container);
                button.href = '#';
                button.title = t('Common.Csango', 'Csángó');
                button.style.backgroundColor = showCsango ? '#8b0000' : 'white';
                button.style.color = showCsango ? 'white' : 'black';
                button.style.width = '30px';
                button.style.height = '30px';
                button.style.display = 'flex';
                button.style.alignItems = 'center';
                button.style.justifyContent = 'center';
                button.style.cursor = 'pointer';
                button.style.fontSize = '12px';
                button.style.fontWeight = 'bold';
                button.innerHTML = 'Cs';

                L.DomEvent.disableClickPropagation(button);
                L.DomEvent.on(button, 'click', function(e) {
                    L.DomEvent.stop(e);
                    setShowCsango(!showCsango);
                });

                setCsangoContainer(button);
                return container;
            },
            onRemove: function() {
                setCsangoContainer(null);
            }
        });

        const fsControl = new FullscreenControl({ position: 'topright' });
        const locControl = new LocationControl({ position: 'topright' });
        const layersControl = new LayersControl({ position: 'topright' });
        const trackerControl = new TrackerControl({ position: 'topright' });
        const csangoControl = new CsangoControl({ position: 'topright' });

        map.addControl(fsControl);
        map.addControl(locControl);
        map.addControl(layersControl);
        map.addControl(csangoControl);
        
        // Only show tracker icon if running is shown and a route is selected
        if (showRunning && selectedInfo?.type === 'running_route') {
            map.addControl(trackerControl);
        }

        return () => {
            map.removeControl(fsControl);
            map.removeControl(locControl);
            map.removeControl(layersControl);
            map.removeControl(csangoControl);
            try {
                map.removeControl(trackerControl);
            } catch (e) {}
        };
    }, [map, userLocation, isLocating, setIsLocating, t, showRunning, selectedInfo, showCsango, setShowCsango]);

    useEffect(() => {
        if (locContainer) {
            locContainer.style.color = isLocating ? '#1976d2' : 'inherit';
        }
    }, [isLocating, locContainer]);

    const toggleFullscreen = () => {
        const mapContainer = map.getContainer().parentElement; 
        const nativeFsElement = document.fullscreenElement || document.webkitFullscreenElement || document.mozFullScreenElement || document.msFullscreenElement;
        const isCurrentlyNativeFs = !!nativeFsElement;

        if (!isCurrentlyNativeFs && !isFullscreen) {
            // Belépés fullscreen módba
            if (mapContainer.requestFullscreen) {
                mapContainer.requestFullscreen();
            } else if (mapContainer.webkitRequestFullscreen) { /* Safari / iOS */
                mapContainer.webkitRequestFullscreen();
            } else if (mapContainer.mozRequestFullScreen) {
                mapContainer.mozRequestFullScreen();
            } else if (mapContainer.msRequestFullscreen) { /* IE11 */
                mapContainer.msRequestFullscreen();
            } else {
                // Pszeudo-fullscreen fallback iOS-re vagy ha nincs API
                setIsFullscreen(true);
            }
        } else {
            // Kilépés fullscreen módból
            if (isCurrentlyNativeFs) {
                try {
                    if (document.exitFullscreen) {
                        document.exitFullscreen();
                    } else if (document.webkitExitFullscreen) {
                        document.webkitExitFullscreen();
                    } else if (document.mozCancelFullScreen) {
                        document.mozCancelFullScreen();
                    } else if (document.msExitFullscreen) {
                        document.msExitFullscreen();
                    }
                } catch (error) {
                    console.error("Error exiting fullscreen:", error);
                }
            }
            
            // Mindenképpen kilépünk a pszeudo-fullscreen állapotból is
            setIsFullscreen(false);
        }
        
        // Kényszerített átméretezés rövid késleltetéssel minden esetben
        setTimeout(() => {
            map.invalidateSize();
        }, 100);
        setTimeout(() => {
            map.invalidateSize();
        }, 500); // 300-ról 500-ra emelve, hogy biztosabb legyen a mobilokon
    };

    return (
        <>
            {layersContainer && createPortal(
                <LayersIcon sx={{ fontSize: '28px' }} />,
                layersContainer
            )}
            {trackerContainer && createPortal(
                <DirectionsRunIcon sx={{ fontSize: '28px' }} color="primary" />,
                trackerContainer
            )}
            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
            >
                <MenuItem 
                    onClick={() => handleMapTypeChange('cyclosm')}
                    selected={mapType === 'cyclosm'}
                >
                    {t('Common.CyclOSM', 'Biciklis térkép')}
                </MenuItem>
                <MenuItem 
                    onClick={() => handleMapTypeChange('herbich')}
                    selected={mapType === 'herbich'}
                >
                    {t('Common.HerbichGeo', 'Herbich-féle földtani térkép')}
                </MenuItem>
                <MenuItem 
                    onClick={() => handleMapTypeChange('nszn')}
                    selected={mapType === 'nszn'}
                >
                    {t('Common.NsznHonismereti', 'NSZN Honismereti térkép')}
                </MenuItem>
                <MenuItem 
                    onClick={() => handleMapTypeChange('geo200000')}
                    selected={mapType === 'geo200000'}
                >
                    {t('Common.Geo200000', 'Geo 200000')}
                </MenuItem>
            </Menu>

            {fsContainer && createPortal(
                isFullscreen ? <FullscreenExitIcon sx={{ fontSize: '28px' }} /> : <FullscreenIcon sx={{ fontSize: '28px' }} />,
                fsContainer
            )}
            {locContainer && createPortal(
                <MyLocationIcon sx={{ fontSize: '28px' }} color="primary" />,
                locContainer
            )}
        </>
    );
};

const MapFocuser = ({ bounds, coords, onComplete }) => {
    const map = useMap();
    useEffect(() => {
        if (bounds || coords) {
            // Rövid késleltetés, hogy a térkép biztosan készen álljon
            const timer = setTimeout(() => {
                map.invalidateSize();
                if (bounds) {
                    map.flyToBounds(bounds, { padding: [50, 50], maxZoom: 15, duration: 1.5 });
                } else if (coords) {
                    map.flyTo(coords, 18, { duration: 1.5 });
                }
                if (onComplete) onComplete();
            }, 300);
            return () => clearTimeout(timer);
        }
    }, [bounds, coords, map, onComplete]);
    return null;
};

const ThematicMap = ({ category, type = 'points' }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();
    const GlobalState = useContext(StateContext);
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [data, setData] = useState(type === 'routes' ? { tourist_routes: [], marked_paths: [] } : { features: [] });
    const [loading, setLoading] = useState(true);
    const [selectedImage, setSelectedImage] = useState(null);
    const [zoom, setZoom] = useState(10);
    const [selectedInfo, setSelectedInfo] = useState(null);
    const [userLocation, setUserLocation] = useState(null);
    const [isLocating, setIsLocating] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [focusedId, setFocusedId] = useState(null);
    const [mapType, setMapType] = useState(category === 'foldtani' ? 'geo200000' : 'cyclosm');
    const [showRunning, setShowRunning] = useState(false);
    const [runningCategory, setRunningCategory] = useState('all');
    const [runningData, setRunningData] = useState([]);
    const [openTracker, setOpenTracker] = useState(false);
    const [isTracking, setIsTracking] = useState(false);
    const [showCsango, setShowCsango] = useState(false);
    const [csangoLoading, setCsangoLoading] = useState(false);
    const [csangoParishes, setCsangoParishes] = useState([]);
    const [csangoTelepulesek, setCsangoTelepulesek] = useState([]);

    useEffect(() => {
        const source = Axios.CancelToken.source();
        async function fetchCsangoData() {
            if (showCsango) {
                setCsangoLoading(true);
                try {
                    // Fetch both Parishes and Telepulesek
                    const pEndpoint = config[environment].csangoKozsegVarosEndpoint(1);
                    const tEndpoint = config[environment].csangoTelepulesEndpoint(1);
                    
                    const [pRes, tRes] = await Promise.all([
                        Axios.get(pEndpoint, { cancelToken: source.token }),
                        Axios.get(tEndpoint, { cancelToken: source.token })
                    ]);
                    
                    setCsangoParishes(pRes.data.results || pRes.data || []);
                    setCsangoTelepulesek(tRes.data.results || tRes.data || []);
                } catch (error) {
                    if (!Axios.isCancel(error)) {
                        console.error("Error fetching Csango data:", error);
                    }
                } finally {
                    setCsangoLoading(false);
                }
            } else {
                setCsangoParishes([]);
                setCsangoTelepulesek([]);
            }
        }
        fetchCsangoData();
        return () => source.cancel();
    }, [showCsango]);

    const getTileLayerProps = () => {
        if (mapType === 'herbich') {
            return {
                url: config[environment].herbichGeoTiles,
                attribution: '&copy; Herbich Geo',
                maxZoom: 18,
                minZoom: 6,
                tms: false,
                opacity: 1,
                noWrap: false,
                bounds: [[44.0, 20.0], [49.0, 30.0]]
            };
        }
        if (mapType === 'nszn') {
            return {
                url: config[environment].nsznHonismeretiTiles,
                attribution: '&copy; NSZN Honismereti',
                maxZoom: 18,
                minZoom: 6,
                tms: false,
                opacity: 1,
                noWrap: false,
                bounds: [[44.0, 20.0], [49.0, 30.0]]
            };
        }
        if (mapType === 'geo200000') {
            return {
                url: config[environment].geo200000Tiles,
                attribution: '&copy; Geo 200000',
                maxZoom: 18,
                minZoom: 6,
                tms: false,
                opacity: 1,
                noWrap: false,
                bounds: [[44.0, 20.0], [49.0, 30.0]]
            };
        }
        return {
            url: config[environment].cyclOSMTiles,
            attribution: 'Map style: <a href="https://github.com/cyclosm/cyclosm-cartocss-style/releases" title="CyclOSM - Open Bicycle render">CyclOSM</a> | Map data: &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
            maxZoom: 18,
            minZoom: 1
        };
    };

    const tileLayerProps = getTileLayerProps();
    const [focusTarget, setFocusTarget] = useState({ bounds: null, coords: null });
    const [selectedHashtag, setSelectedHashtag] = useState(null);
    const [allHashtags, setAllHashtags] = useState([]);
    const mapRef = useRef(null);

    const runnerIcon = useMemo(() => L.divIcon({
        html: renderToStaticMarkup(
            <DirectionsRunIcon style={{ color: 'magenta', fontSize: '24px', backgroundColor: 'white', borderRadius: '50%', border: '2px solid magenta' }} />
        ),
        iconSize: [24, 24],
        iconAnchor: [12, 12],
        className: 'runner-icon-div'
    }), []);

    // Kinyerjük az összes hashtaget az adatokból
    useEffect(() => {
        if (data?.features) {
            const tags = new Set();
            data.features.forEach(f => {
                if (f.properties?.hashtags) {
                    const tagList = f.properties.hashtags.split(/[\s,]+/).filter(t => t.startsWith('#'));
                    tagList.forEach(t => tags.add(t));
                }
            });
            setAllHashtags(Array.from(tags).sort());
        }
    }, [data, category]);

    useEffect(() => {
        const source = Axios.CancelToken.source();
        const fetchRunningRoutes = async () => {
            if (showRunning) {
                try {
                    let url = config[environment].runningRoutesEndpoint;
                    if (runningCategory !== 'all') {
                        url += `&running_category=${encodeURIComponent(runningCategory)}`;
                    }
                    const response = await Axios.get(url, { cancelToken: source.token });
                    const newFeatures = response.data.features || response.data || [];
                    setRunningData(newFeatures);
                    
                    // Reset selectedInfo if it's a running route that is no longer in the list
                    setSelectedInfo(prev => {
                        if (prev?.type === 'running_route') {
                            const stillExists = newFeatures.some(r => {
                                const rid = r.id || r.properties?.id;
                                return String(rid) === String(prev.id) || 
                                       (prev.name && r.properties?.name === prev.name);
                            });
                            return stillExists ? prev : null;
                        }
                        return prev;
                    });
                } catch (error) {
                    if (!Axios.isCancel(error)) console.error("Running routes fetch error:", error);
                }
            } else {
                setRunningData([]);
                setSelectedInfo(prev => prev?.type === 'running_route' ? null : prev);
            }
        };
        fetchRunningRoutes();
        return () => source.cancel();
    }, [showRunning, runningCategory, config, environment]);

    useEffect(() => {
        const handleFullscreenChange = () => {
            const nativeFsElement = document.fullscreenElement || document.webkitFullscreenElement || document.mozFullScreenElement || document.msFullscreenElement;
            const isNativeFs = !!nativeFsElement;
            setIsFullscreen(isNativeFs);
            
            if (!isNativeFs) {
                setTimeout(() => {
                    const map = mapRef.current;
                    if (map) {
                        const mapInstance = map.getLeafletElement ? map.getLeafletElement() : map;
                        if (mapInstance.invalidateSize) mapInstance.invalidateSize();
                    }
                }, 100);
            }
        };
        document.addEventListener('fullscreenchange', handleFullscreenChange);
        document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
        document.addEventListener('mozfullscreenchange', handleFullscreenChange);
        document.addEventListener('MSFullscreenChange', handleFullscreenChange);
        
        return () => {
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
            document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
            document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
            document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
        };
    }, []);

    useEffect(() => {
        const map = mapRef.current;
        const mapElement = map ? (map.getContainer ? map.getContainer() : (map.getLeafletElement ? map.getLeafletElement().getContainer() : null)) : null;
        if (!mapElement) return;

        const parentContainer = mapElement.closest('.thematic-map-container');

        if (isFullscreen) {
            const nativeFsElement = document.fullscreenElement || document.webkitFullscreenElement || document.mozFullScreenElement || document.msFullscreenElement;
            
            if (!nativeFsElement) {
                if (parentContainer) {
                    parentContainer.style.setProperty('position', 'fixed', 'important');
                    parentContainer.style.setProperty('top', '0', 'important');
                    parentContainer.style.setProperty('left', '0', 'important');
                    parentContainer.style.setProperty('width', '100vw', 'important');
                    parentContainer.style.setProperty('height', '100dvh', 'important');
                    parentContainer.style.setProperty('z-index', '9999', 'important');
                    parentContainer.style.setProperty('margin-top', '0', 'important');
                } else {
                    mapElement.style.setProperty('position', 'fixed', 'important');
                    mapElement.style.setProperty('top', '0', 'important');
                    mapElement.style.setProperty('left', '0', 'important');
                    mapElement.style.setProperty('width', '100vw', 'important');
                    mapElement.style.setProperty('height', '100dvh', 'important');
                    mapElement.style.setProperty('z-index', '9999', 'important');
                }
                
                document.body.style.overflow = 'hidden';
            }
        } else {
            if (parentContainer) {
                parentContainer.style.removeProperty('position');
                parentContainer.style.removeProperty('top');
                parentContainer.style.removeProperty('left');
                parentContainer.style.removeProperty('width');
                parentContainer.style.removeProperty('height');
                parentContainer.style.removeProperty('z-index');
                parentContainer.style.removeProperty('margin-top');
            }
            mapElement.style.removeProperty('position');
            mapElement.style.removeProperty('top');
            mapElement.style.removeProperty('left');
            mapElement.style.removeProperty('width');
            mapElement.style.removeProperty('height');
            mapElement.style.removeProperty('z-index');
            
            document.body.style.overflow = '';
        }

        return () => {
            if (parentContainer) {
                parentContainer.style.removeProperty('position');
                parentContainer.style.removeProperty('top');
                parentContainer.style.removeProperty('left');
                parentContainer.style.removeProperty('width');
                parentContainer.style.removeProperty('height');
                parentContainer.style.removeProperty('z-index');
                parentContainer.style.removeProperty('margin-top');
            }
            if (mapElement) {
                mapElement.style.removeProperty('position');
                mapElement.style.removeProperty('top');
                mapElement.style.removeProperty('left');
                mapElement.style.removeProperty('width');
                mapElement.style.removeProperty('height');
                mapElement.style.removeProperty('z-index');
            }
            document.body.style.overflow = '';
        };
    }, [isFullscreen]);

    useEffect(() => {
        if (category === 'foldtani') {
            setMapType('geo200000');
        } else {
            setMapType('cyclosm');
        }
    }, [category]);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                let url;
                if (type === 'routes') {
                    url = `${config[environment].apiEndpoint}api/thematic-maps/tourist-routes/`;
                } else {
                    url = `${config[environment].apiEndpoint}api/thematic-maps/points/?category=${category}`;
                    if (selectedHashtag) {
                        url += `&hashtag=${encodeURIComponent(selectedHashtag)}`;
                    }
                }
                const response = await Axios.get(url);
                setData(response.data);
            } catch (error) {
                // Error handled by UI
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [category, type, selectedHashtag]);

    useEffect(() => {
        if (!loading && (location.state?.focusRoute || location.state?.focusPoint)) {
            let foundFeature = null;
            let targetBounds = null;
            let targetCoords = null;

            if (location.state?.focusRoute) {
                const routeId = location.state.focusRoute;
                setFocusedId(routeId);
                
                if (type === 'routes') {
                    foundFeature = data.tourist_routes?.features?.find(f => 
                        String(f.id) === String(routeId) || (f.properties && String(f.properties.id) === String(routeId))
                    );
                }

                if (foundFeature && foundFeature.geometry) {
                    let allCoords = [];
                    if (foundFeature.geometry.type === 'MultiLineString') {
                        allCoords = foundFeature.geometry.coordinates.flat(1);
                    } else if (foundFeature.geometry.type === 'LineString') {
                        allCoords = foundFeature.geometry.coordinates;
                    }
                    
                    if (allCoords && allCoords.length > 0) {
                        targetBounds = L.latLngBounds(allCoords.map(c => [c[1], c[0]]));
                        setSelectedInfo({ ...foundFeature.properties, type: 'tourist_route', id: routeId });
                    }
                }
            } else if (location.state?.focusPoint) {
                const pointId = location.state.focusPoint;
                setFocusedId(pointId);
                foundFeature = data.features?.find(f => 
                    String(f.id) === String(pointId) || (f.properties && String(f.properties.id) === String(pointId))
                );
                
                if (foundFeature && foundFeature.geometry && foundFeature.geometry.coordinates) {
                    targetCoords = [foundFeature.geometry.coordinates[1], foundFeature.geometry.coordinates[0]];
                    setSelectedInfo({ ...foundFeature.properties, type: 'point', id: pointId });
                }
            }

            if (targetBounds || targetCoords) {
                setFocusTarget({ bounds: targetBounds, coords: targetCoords });
                
                // Ha mobil, görgetünk az infó panelhez
                if (isMobile) {
                    setTimeout(() => {
                        const infoPanel = document.getElementById('thematic-info-panel');
                        if (infoPanel) infoPanel.scrollIntoView({ behavior: 'smooth' });
                    }, 1000);
                }
                
                // Állapot ürítése a navigáció után
                navigate(location.pathname, { replace: true, state: {} });
            }
        }
    }, [loading, location.state, data, type, isMobile, navigate, location.pathname]);

    const getSelectPrompt = () => {
        if (type === 'routes') return t('Thematic.SelectRoutePrompt');
        
        switch (category) {
            case 'panteon':
                return t('Thematic.SelectPersonPrompt');
            case 'nepviselet':
                return t('Thematic.SelectCostumePrompt');
            case 'nepi_epiteszet':
                return t('Thematic.SelectArchPrompt');
            case 'szekelykapu':
                return t('Thematic.SelectGatePrompt');
            case 'ballada':
                return t('Thematic.SelectBalladPrompt');
            case 'nepdal':
                return t('Thematic.SelectFolkSongPrompt');
            case 'locsoloversek':
                return t('Thematic.SelectVersePrompt');
            case 'futuristique':
                return t('Thematic.SelectFuturePrompt');
            case 'foldtani':
                return t('Thematic.SelectFoldtaniPrompt');
            default:
                return t('Thematic.SelectPrompt');
        }
    };

    // Pontok csoportosítása koordináta alapján és eltolás számítása
    const groupedPoints = useMemo(() => {
        if (type === 'routes' || !data?.features) return [];
        
        const filteredFeatures = showCsango 
            ? data.features.filter(f => f.properties.is_csango) 
            : data.features.filter(f => !f.properties.is_csango);

        const groups = {};
        filteredFeatures.forEach(feature => {
            const coordKey = `${feature.geometry.coordinates[1].toFixed(5)},${feature.geometry.coordinates[0].toFixed(5)}`;
            if (!groups[coordKey]) {
                groups[coordKey] = [];
            }
            groups[coordKey].push(feature);
        });

        const result = [];
        Object.keys(groups).forEach(key => {
            const features = groups[key];
            features.forEach((feature, index) => {
                let offsetPos = [feature.geometry.coordinates[1], feature.geometry.coordinates[0]];
                
                if (features.length > 1) {
                    // Ha több pont van ugyanott, eltoljuk őket egy kör mentén
                    // Az eltolás mértéke függ a zoom szinttől (később a renderelésnél)
                    // Itt csak az indexet és az összes pont számát tároljuk el
                    feature._groupIndex = index;
                    feature._groupCount = features.length;
                } else {
                    feature._groupIndex = 0;
                    feature._groupCount = 1;
                }
                result.push(feature);
            });
        });
        return result;
    }, [data, type, showCsango]);

    const getOffsetPosition = (basePos, index, count, currentZoom) => {
        if (count <= 1) return basePos;
        
        const angle = (index / count) * 2 * Math.PI;
        
        // Pixel-eltolás sugarának kiszámítása
        // Nagyobb zoom (közelebb) -> kisebb koordináta-eltolás kell ugyanahhoz a pixel-távolsághoz
        // Alap sugár 40 pixel, ami elég a 60-80 pixel széles ikonok eltolásához
        let radiusInPixels = 40 + (count > 5 ? 20 : 0);
        
        // Ha nagyon ki van zoomolva, csökkentjük a pixel-sugarat, hogy ne legyen túl szétszórt
        if (currentZoom < 10) radiusInPixels *= (currentZoom / 10);
        
        // Web Mercator közelítés: 1 fok lat ~ 111km, 1 fok lng ~ 111km * cos(lat)
        const metersPerPixel = 156543.03392 * Math.cos(basePos[0] * Math.PI / 180) / Math.pow(2, currentZoom);
        const radiusInMeters = radiusInPixels * metersPerPixel;
        
        const offsetLat = (radiusInMeters * Math.cos(angle)) / 111320;
        const offsetLng = (radiusInMeters * Math.sin(angle)) / (111320 * Math.cos(basePos[0] * Math.PI / 180));
        
        return [basePos[0] + offsetLat, basePos[1] + offsetLng];
    };

    const isSelectedRunningRoute = (route) => {
        if (selectedInfo?.type !== 'running_route') return false;
        
        const routeId = route.id;
        const propId = route.properties?.id;
        const routeName = route.properties?.name;
        
        const selId = selectedInfo.id;
        const selName = selectedInfo.name;

        // Identification logic using multiple sources (root ID, property ID, name)
        // String conversion is used to handle mixed types (number vs string)
        if (selId !== undefined && selId !== null && selId !== '') {
            if (routeId !== undefined && routeId !== null && String(selId) === String(routeId)) return true;
            if (propId !== undefined && propId !== null && String(selId) === String(propId)) return true;
        }
        
        if (selName && routeName && String(selName) === String(routeName)) return true;
        
        return false;
    };

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: '114px' }}><CircularProgress /></Box>;

    return (
        <Box className="thematic-map-container" sx={{ 
            height: { xs: 'calc(100vh - 64px)', md: 'calc(100vh - 64px - 50px)' }, 
            width: '100%', 
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            mt: { xs: 0, md: '50px' }
        }}>
            {/* Map Block (3/4 desktop, auto mobile) */}
            <Box sx={{ 
                flex: isMobile ? 'none' : 3, 
                height: isMobile ? '60vh' : '100%',
                position: 'relative',
                '& .leaflet-container': {
                    height: '100%',
                    width: '100%'
                }
            }}>
                <MapContainer 
                    ref={mapRef}
                    center={[45.8643, 25.7848]} 
                    zoom={10} 
                    style={{ height: '100%', width: '100%' }} 
                    zoomControl={false}
                >
                    <ZoomControl position="topleft" />
                    <ZoomHandler 
                        setZoom={setZoom} 
                        userLocation={userLocation} 
                        setUserLocation={setUserLocation}
                        isFullscreen={isFullscreen}
                        isMobile={isMobile}
                        isLocating={isLocating}
                        isTracking={isTracking}
                    />
                    <MapControls 
                        isMobile={isMobile} 
                        userLocation={userLocation} 
                        isLocating={isLocating}
                        setIsLocating={setIsLocating}
                        isFullscreen={isFullscreen}
                        setIsFullscreen={setIsFullscreen}
                        mapType={mapType}
                        setMapType={setMapType}
                        showRunning={showRunning}
                        selectedInfo={selectedInfo}
                        setOpenTracker={setOpenTracker}
                        showCsango={showCsango}
                        setShowCsango={setShowCsango}
                    />
                    <MapFocuser 
                        bounds={focusTarget.bounds} 
                        coords={focusTarget.coords} 
                        onComplete={() => setFocusTarget({ bounds: null, coords: null })}
                    />
                    
                    <Box className="top-toolbox" sx={{ 
                        top: isMobile ? '10px' : '20px', 
                        left: isMobile ? '50%' : '200px',
                        transform: isMobile ? 'translateX(-50%)' : 'none',
                        display: type === 'routes' ? 'flex' : 'none', 
                        alignItems: 'center', 
                        zIndex: 1000,
                        position: 'absolute',
                        width: 'auto',
                        maxWidth: '90%'
                    }}>
                        <Box sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            backgroundColor: 'rgba(255,255,255,0.8)',
                            p: '0 8px',
                            borderRadius: '4px',
                            whiteSpace: 'nowrap'
                        }}>
                            <FormControlLabel
                                control={
                                    <Checkbox 
                                        size="small"
                                        checked={showRunning} 
                                        onChange={(e) => setShowRunning(e.target.checked)} 
                                        sx={{ p: 0.5 }}
                                    />
                                }
                                label={t('Map.Toolbox.running', 'Futás')}
                                sx={{ m: 0 }}
                            />
                        </Box>
                        {showRunning && (
                            <Box sx={{
                                ml: 1,
                                backgroundColor: 'rgba(255,255,255,0.8)',
                                p: '0 8px',
                                borderRadius: '4px',
                                display: 'inline-flex',
                                alignItems: 'center',
                                height: '38px', // Matching height with checkbox container
                                whiteSpace: 'nowrap'
                            }}>
                                <Select
                                    size="small"
                                    value={runningCategory}
                                    onChange={(e) => setRunningCategory(e.target.value)}
                                    sx={{ height: '30px', fontSize: '0.8rem' }}
                                >
                                    <MenuItem value="all">{t('Map.Running.All', 'Összes')}</MenuItem>
                                    <MenuItem value="10K City">10K City</MenuItem>
                                    <MenuItem value="10K Cross">10K Cross</MenuItem>
                                    <MenuItem value="5K City">5K City</MenuItem>
                                    <MenuItem value="Félmaraton">Félmaraton</MenuItem>
                                    <MenuItem value="Maraton">Maraton</MenuItem>
                                </Select>
                            </Box>
                        )}
                    </Box>

                    <TileLayer
                        key={mapType}
                        attribution={tileLayerProps.attribution}
                        url={tileLayerProps.url}
                        maxZoom={tileLayerProps.maxZoom}
                        minZoom={tileLayerProps.minZoom}
                        tms={tileLayerProps.tms}
                        opacity={tileLayerProps.opacity}
                        noWrap={tileLayerProps.noWrap}
                    />

                    {/* Csango Administrative Layers */}
                    {showCsango && (
                        <>
                            {csangoParishes.map((parish) => (
                                parish.geom && (
                                    <Polygon
                                        key={`csango-parish-${parish.id || parish.pk}`}
                                        positions={L.GeoJSON.coordsToLatLngs(parish.geom.coordinates, 2)}
                                        pathOptions={{ color: '#8b0000', weight: 2, fillOpacity: 0.1 }}
                                    >
                                        <Tooltip sticky>{parish.nev}</Tooltip>
                                    </Polygon>
                                )
                            ))}
                            {csangoTelepulesek.map((telepules) => (
                                telepules.geom && (
                                    <Polygon
                                        key={`csango-telepules-${telepules.id || telepules.pk}`}
                                        positions={L.GeoJSON.coordsToLatLngs(telepules.geom.coordinates, 2)}
                                        pathOptions={{ color: '#8b0000', weight: 1, fillOpacity: 0.05, dashArray: '5, 5' }}
                                    >
                                        <Tooltip sticky>{telepules.nev}</Tooltip>
                                    </Polygon>
                                )
                            ))}
                        </>
                    )}

                    {showRunning && Array.isArray(runningData) && runningData.map((route, idx) => {
                        if (!route || !route.geometry || route.geometry.type !== 'MultiLineString') return null;
                        const positions = route.geometry.coordinates.map(line => line.map(coord => [coord[1], coord[0]]));
                        // Get the middle point of the first line for the runner icon
                        if (!positions[0] || positions[0].length === 0) return null;
                        const midPointIndex = Math.floor(positions[0].length / 2);
                        const midPoint = positions[0][midPointIndex];
                        if (!midPoint) return null;

                        const isHighlighted = isSelectedRunningRoute(route);

                        return (
                            <React.Fragment key={`running-${route.id || idx}`}>
                                <Polyline 
                                    key={`polyline-${route.id || idx}-${isHighlighted}`}
                                    positions={positions} 
                                    color={isHighlighted ? "yellow" : "magenta"} 
                                    weight={isHighlighted ? 8 : 4}
                                    dashArray={isHighlighted ? "0" : "5, 10"}
                                    interactive={true}
                                    bubblingMouseEvents={false}
                                    style={{ zIndex: isHighlighted ? 1000 : 1 }}
                                    eventHandlers={{
                                        click: (e) => {
                                            const routeId = route.id || (route.properties && route.properties.id);
                                            const routeName = route.properties && route.properties.name;
                                            setSelectedInfo({ 
                                                ...(route.properties || {}), 
                                                id: routeId, 
                                                name: routeName,
                                                type: 'running_route' 
                                            });
                                            L.DomEvent.stopPropagation(e);
                                            if (isMobile) {
                                                setTimeout(() => {
                                                    const infoPanel = document.getElementById('thematic-info-panel');
                                                    if (infoPanel) {
                                                        infoPanel.scrollIntoView({ behavior: 'smooth' });
                                                    }
                                                }, 100);
                                            }
                                        }
                                    }}
                                >
                                    {isHighlighted && <Tooltip>{route.properties.name}</Tooltip>}
                                    <Popup>
                                        <Typography variant="subtitle2"><b>{route.properties.name}</b></Typography>
                                        <Typography variant="body2">{route.properties.category}</Typography>
                                        {route.properties.distance && <Typography variant="caption">Táv: {route.properties.distance} km</Typography>}
                                        <Typography variant="body2" sx={{ mt: 1 }}>{route.properties.description}</Typography>
                                    </Popup>
                                </Polyline>
                                {!(isTracking && isHighlighted) && (
                                    <Marker position={midPoint} icon={runnerIcon} interactive={false} />
                                )}
                            </React.Fragment>
                        );
                    })}

                    {userLocation && (
                        <>
                            <Circle
                                center={[userLocation.lat, userLocation.lng]}
                                radius={20}
                                pathOptions={{ 
                                    color: isTracking ? 'magenta' : 'blue', 
                                    fillColor: isTracking ? 'magenta' : 'blue', 
                                    fillOpacity: 0.5 
                                }}
                            >
                                <Popup>{isTracking ? t('Running.Tracker.YourPosition', 'Aktuális pozíciód') : t('Map.YouAreHere', 'Te itt vagy')}</Popup>
                            </Circle>
                            {isTracking && (
                                <Marker position={[userLocation.lat, userLocation.lng]} icon={runnerIcon} zIndexOffset={1000} />
                            )}
                            {!isTracking && isLocating && (
                                <Marker position={[userLocation.lat, userLocation.lng]} icon={locationIcon} zIndexOffset={1000} />
                            )}
                        </>
                    )}

                    {type === 'routes' ? (
                        <>
                            {Array.isArray(data.tourist_routes?.features) && data.tourist_routes.features.map((feature) => {
                                if (!feature || !feature.geometry) return null;
                                const positions = feature.geometry.type === 'MultiLineString' 
                                    ? feature.geometry.coordinates.map(line => line.map(c => [c[1], c[0]]))
                                    : feature.geometry.coordinates.map(c => [c[1], c[0]]);

                                return (
                                    <React.Fragment key={`route-wrapper-${feature.id || (feature.properties && feature.properties.id)}`}>
                                        {/* Hit area for easier clicking */}
                                        <Polyline
                                            positions={positions}
                                            pathOptions={{ color: 'transparent', weight: 20 }}
                                            eventHandlers={{
                                                click: (e) => {
                                                    setFocusedId(feature.id || (feature.properties && feature.properties.id));
                                                    setSelectedInfo({ ...feature.properties, type: 'tourist_route' });
                                                    L.DomEvent.stopPropagation(e);
                                                    if (isMobile) {
                                                        setTimeout(() => {
                                                            const infoPanel = document.getElementById('thematic-info-panel');
                                                            if (infoPanel) {
                                                                infoPanel.scrollIntoView({ behavior: 'smooth' });
                                                            }
                                                        }, 100);
                                                    }
                                                }
                                            }}
                                        />
                                        <Polyline
                                            key={`route-${feature.id || (feature.properties && feature.properties.id)}-${String(focusedId) === String(feature.id || (feature.properties && feature.properties.id))}`}
                                            positions={positions}
                                            color={String(focusedId) === String(feature.id || (feature.properties && feature.properties.id)) ? '#d32f2f' : getRouteColor(feature.properties.jelzes || feature.properties.szimbolum)}
                                            weight={String(focusedId) === String(feature.id || (feature.properties && feature.properties.id)) ? 8 : 4}
                                            opacity={String(focusedId) === String(feature.id || (feature.properties && feature.properties.id)) ? 1 : 0.8}
                                            style={{ zIndex: String(focusedId) === String(feature.id || (feature.properties && feature.properties.id)) ? 1000 : 1 }}
                                            eventHandlers={{
                                                click: (e) => {
                                                    setSelectedInfo({ ...feature.properties, type: 'tourist_route' });
                                                    e.target.closePopup();
                                                    if (isMobile) {
                                                        setTimeout(() => {
                                                            const infoPanel = document.getElementById('thematic-info-panel');
                                                            if (infoPanel) {
                                                                infoPanel.scrollIntoView({ behavior: 'smooth' });
                                                            }
                                                        }, 100);
                                                    }
                                                },
                                                mouseover: (e) => !isMobile && e.target.openPopup(),
                                                mouseout: (e) => !isMobile && e.target.closePopup(),
                                            }}
                                        >
                                            <Popup closeButton={false} autoPan={false}>
                                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>{feature.properties.nev}</Typography>
                                                {feature.properties.leiras && <Typography variant="body2">{feature.properties.leiras}</Typography>}
                                                {feature.properties.hossza && <Typography variant="caption" display="block">{t('Events.Length', 'Hossz')}: {feature.properties.hossza} km</Typography>}
                                                {feature.properties.nehezseg && <Typography variant="caption" display="block">{t('Thematic.Difficulty', 'Nehézség')}: {feature.properties.nehezseg}</Typography>}
                                            </Popup>
                                        </Polyline>
                                    </React.Fragment>
                                );
                            })}
                            {Array.isArray(data.marked_paths?.features) && data.marked_paths.features.map((feature) => {
                                if (!feature || !feature.geometry) return null;
                                const positions = feature.geometry.type === 'MultiLineString' 
                                    ? feature.geometry.coordinates.map(line => line.map(c => [c[1], c[0]]))
                                    : feature.geometry.coordinates.map(c => [c[1], c[0]]);
                                
                                const iconPoints = getPointsAlongPath(positions, zoom, feature.properties.szimbolum);
                                const markIcon = getMarkIcon(feature.properties.szimbolum, zoom, isMobile);

                                return (
                                    <React.Fragment key={`marked-wrapper-${feature.id}`}>
                                        {/* Hit area for easier clicking */}
                                        <Polyline
                                            positions={positions}
                                            pathOptions={{ color: 'transparent', weight: 20 }}
                                            eventHandlers={{
                                                click: (e) => {
                                                    setSelectedInfo({ ...feature.properties, type: 'marked_path' });
                                                    L.DomEvent.stopPropagation(e);
                                                    if (isMobile) {
                                                        setTimeout(() => {
                                                            const infoPanel = document.getElementById('thematic-info-panel');
                                                            if (infoPanel) {
                                                                infoPanel.scrollIntoView({ behavior: 'smooth' });
                                                            }
                                                        }, 100);
                                                    }
                                                }
                                            }}
                                        />
                                        <Polyline
                                            positions={positions}
                                            color={getRouteColor(feature.properties.szimbolum)}
                                            weight={focusedId === feature.id ? 6 : 3}
                                            dashArray={focusedId === feature.id ? "0" : "10, 10"}
                                            opacity={focusedId === feature.id ? 1 : 0.7}
                                            eventHandlers={{
                                                click: (e) => {
                                                    setSelectedInfo({ ...feature.properties, type: 'marked_path' });
                                                    e.target.closePopup();
                                                    if (isMobile) {
                                                        setTimeout(() => {
                                                            const infoPanel = document.getElementById('thematic-info-panel');
                                                            if (infoPanel) {
                                                                infoPanel.scrollIntoView({ behavior: 'smooth' });
                                                            }
                                                        }, 100);
                                                    }
                                                },
                                                mouseover: (e) => !isMobile && e.target.openPopup(),
                                                mouseout: (e) => !isMobile && e.target.closePopup(),
                                            }}
                                        >
                                            <Popup closeButton={false} autoPan={false}>
                                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>{feature.properties.nev}</Typography>
                                                <Typography variant="body2">{t('Events.Category', 'Jelzés')}: {feature.properties.szimbolum}</Typography>
                                                {feature.properties.hossza && <Typography variant="caption" display="block">{t('Events.Length', 'Hossz')}: {feature.properties.hossza} km</Typography>}
                                            </Popup>
                                        </Polyline>
                                        {markIcon && iconPoints.map((point, idx) => (
                                            <Marker 
                                                key={`${feature.id}-icon-${idx}`} 
                                                position={point} 
                                                icon={markIcon}
                                                eventHandlers={{
                                                    click: (e) => {
                                                        setSelectedInfo({ ...feature.properties, type: 'marked_path' });
                                                        e.target.closePopup();
                                                        if (isMobile) {
                                                            setTimeout(() => {
                                                                window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
                                                            }, 100);
                                                        }
                                                    },
                                                    mouseover: (e) => !isMobile && e.target.openPopup(),
                                                    mouseout: (e) => !isMobile && e.target.closePopup(),
                                                }}
                                            >
                                                <Popup closeButton={false} autoPan={false}>
                                                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>{feature.properties.nev}</Typography>
                                                    <Typography variant="body2">{t('Events.Category', 'Jelzés')}: {feature.properties.szimbolum}</Typography>
                                                </Popup>
                                            </Marker>
                                        ))}
                                    </React.Fragment>
                                );
                            })}
                        </>
                    ) : (
                        Array.isArray(groupedPoints) && groupedPoints.map((feature) => {
                            // Helper to normalize coordinates (handle potential [lng, lat] vs [lat, lng])
                            const basePosition = [feature.geometry.coordinates[1], feature.geometry.coordinates[0]];
                            const position = getOffsetPosition(basePosition, feature._groupIndex, feature._groupCount, zoom);
                            
                            const thumbnailUrl = feature.properties.thumbnail 
                                ? (feature.properties.thumbnail.startsWith('http') 
                                    ? feature.properties.thumbnail 
                                    : `${config[environment].apiEndpoint}${feature.properties.thumbnail.startsWith('/') ? feature.properties.thumbnail.substring(1) : feature.properties.thumbnail}`)
                                : null;
                            
                            const imageUrl = feature.properties.image
                                ? (feature.properties.image.startsWith('http')
                                    ? feature.properties.image
                                    : `${config[environment].apiEndpoint}${feature.properties.image.startsWith('/') ? feature.properties.image.substring(1) : feature.properties.image}`)
                                : null;
                            
                            // Use imageUrl as fallback for thumbnail if thumbnail is missing
                            const finalThumbnailUrl = (thumbnailUrl && thumbnailUrl !== imageUrl) ? thumbnailUrl : imageUrl;

                            const icon = (category === 'ballada' || category === 'nepdal' || category === 'locsoloversek' || finalThumbnailUrl) 
                                ? createThumbnailIcon(finalThumbnailUrl, category, isMobile) 
                                : defaultIcon;
                            const isFocused = focusedId === feature.id || (feature.properties && focusedId === feature.properties.id);

                            return (
                                <Marker
                                    key={`point-${feature.id || (feature.properties && feature.properties.id)}`}
                                    position={position}
                                    icon={icon}
                                    zIndexOffset={isFocused ? 1000 : 0}
                                    eventHandlers={{
                                        click: (e) => {
                                            setFocusedId(feature.id || (feature.properties && (feature.properties.id || feature.properties.pk)));
                                            setSelectedInfo({ ...feature.properties, type: 'point', id: feature.id || (feature.properties && (feature.properties.id || feature.properties.pk)) });
                                            e.target.closePopup();
                                            if (isMobile) {
                                                setTimeout(() => {
                                                    const infoPanel = document.getElementById('thematic-info-panel');
                                                    if (infoPanel) {
                                                        infoPanel.scrollIntoView({ behavior: 'smooth' });
                                                    }
                                                }, 100);
                                            }
                                        },
                                        mouseover: (e) => !isMobile && e.target.openPopup(),
                                        mouseout: (e) => !isMobile && e.target.closePopup(),
                                    }}
                                >
                                    <Popup closeButton={false} autoPan={false}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>{feature.properties.name}</Typography>
                                        {thumbnailUrl && thumbnailUrl !== imageUrl && (
                                            <img
                                                src={thumbnailUrl}
                                                alt={feature.properties.name}
                                                style={{ width: '100px', height: 'auto', cursor: 'pointer', display: 'block', margin: '5px 0' }}
                                                onClick={() => setSelectedImage(imageUrl)}
                                            />
                                        )}
                                        {!thumbnailUrl && imageUrl && (
                                            <Typography variant="body2" sx={{ fontStyle: 'italic', my: 1 }}>
                                                {t('Thematic.ImageLoading', 'Kép betöltése...')}
                                            </Typography>
                                        )}
                                        {category?.toLowerCase() !== 'ballada' && category?.toLowerCase() !== 'nepdal' && category?.toLowerCase() !== 'locsoloversek' && (
                                            <Typography variant="body2">{feature.properties.description}</Typography>
                                        )}
                                        {(category?.toLowerCase() === 'ballada' || category?.toLowerCase() === 'nepdal' || category?.toLowerCase() === 'locsoloversek') && (
                                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                                {feature.properties.description?.split('\n').slice(0, 4).join('\n')}
                                                {feature.properties.description?.split('\n').length > 4 ? '...' : ''}
                                            </Typography>
                                        )}
                                        
                                        {/* Folk Architecture specific details in Popup */}
                                        {category === 'nepi_epiteszet' && (
                                            <Box sx={{ mt: 1, mb: 1 }}>
                                                {feature.properties.address && <Typography variant="caption" display="block"><strong>{t('Common.Address', 'Cím')}:</strong> {feature.properties.address}</Typography>}
                                                {feature.properties.built_year && <Typography variant="caption" display="block"><strong>{t('Thematic.BuiltYear', 'Épült')}:</strong> {feature.properties.built_year}</Typography>}
                                            </Box>
                                        )}

                                        {/* Székelykapu specific details in Popup */}
                                        {category === 'szekelykapu' && (
                                            <Box sx={{ mt: 1, mb: 1 }}>
                                                {feature.properties.erected_by && <Typography variant="caption" display="block"><strong>{t('Thematic.ErectedBy', 'Állíttatta')}:</strong> {feature.properties.erected_by}</Typography>}
                                                {feature.properties.erection_year && <Typography variant="caption" display="block"><strong>{t('Thematic.ErectionYear', 'A kapuállítás éve')}:</strong> {feature.properties.erection_year}</Typography>}
                                            </Box>
                                        )}

                                        {/* Folk Costume specific details */}
                                        {category === 'nepviselet' && (
                                            <Box sx={{ mt: 1, mb: 1 }}>
                                                {feature.properties.collection_year && <Typography variant="caption" display="block"><strong>{t('Thematic.CollectionYear', 'Gyűjtés éve')}:</strong> {feature.properties.collection_year}</Typography>}
                                                {feature.properties.collected_by && <Typography variant="caption" display="block"><strong>{t('Thematic.CollectedBy', 'Gyűjtő neve')}:</strong> {feature.properties.collected_by}</Typography>}
                                                {feature.properties.costume_type && <Typography variant="caption" display="block"><strong>{t('Thematic.CostumeType', 'Viselet típusa')}:</strong> {feature.properties.costume_type}</Typography>}
                                            </Box>
                                        )}

                                        {/* Ballad / Folk Song / Verse specific details in Popup */}
                                        {(category === 'ballada' || category === 'nepdal' || category === 'locsoloversek') && (
                                            <Box sx={{ mt: 1, mb: 1 }}>
                                                {feature.properties.motif_marker && <Typography variant="caption" display="block"><strong>{t('Thematic.MotifMarker', 'Motívumjelölő')}:</strong> {feature.properties.motif_marker}</Typography>}
                                                {feature.properties.informant && <Typography variant="caption" display="block"><strong>{t('Thematic.Informant', 'Adatközlő')}:</strong> {feature.properties.informant}</Typography>}
                                                {feature.properties.collected_by && <Typography variant="caption" display="block"><strong>{t('Thematic.CollectedBy', 'Gyűjtötte')}:</strong> {feature.properties.collected_by}</Typography>}
                                            </Box>
                                        )}
                                        
                                        {/* Foldtani specific details in Popup */}
                                        {category === 'foldtani' && (
                                            <Box sx={{ mt: 1, mb: 1 }}>
                                                {feature.properties.stratigraphic_age && <Typography variant="caption" display="block"><strong>{t('Thematic.StratigraphicAge', 'Rétegtani kor')}:</strong> {feature.properties.stratigraphic_age}</Typography>}
                                                {feature.properties.stratigraphic_formation && <Typography variant="caption" display="block"><strong>{t('Thematic.StratigraphicFormation', 'Rétegtani formáció')}:</strong> {feature.properties.stratigraphic_formation}</Typography>}
                                                {feature.properties.rock_type && <Typography variant="caption" display="block"><strong>{t('Thematic.RockType', 'Kőzettípus')}:</strong> {feature.properties.rock_type}</Typography>}
                                                {feature.properties.characteristic_mineral && <Typography variant="caption" display="block"><strong>{t('Thematic.CharacteristicMineral', 'Jellegzetes ásvány')}:</strong> {feature.properties.characteristic_mineral}</Typography>}
                                                {feature.properties.characteristic_fossil && <Typography variant="caption" display="block"><strong>{t('Thematic.CharacteristicFossil', 'Jellegzetes kövület')}:</strong> {feature.properties.characteristic_fossil}</Typography>}
                                                {feature.properties.description && (
                                                    <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                                                        <strong>{t('Common.Description', 'Leírás')}:</strong> {feature.properties.description}
                                                    </Typography>
                                                )}
                                            </Box>
                                        )}
                                        
                                        {feature.properties.hashtags && (
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                                                {feature.properties.hashtags.split(/[\s,]+/).filter(t => t.startsWith('#')).map(tag => (
                                                    <Typography 
                                                        key={tag} 
                                                        variant="caption" 
                                                        sx={{ color: '#105b79', fontWeight: 'bold', cursor: 'pointer' }}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setSelectedHashtag(tag);
                                                        }}
                                                    >
                                                        {tag}
                                                    </Typography>
                                                ))}
                                            </Box>
                                        )}
                                        
                                        <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                                            {feature.properties.gallery_link && (
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    onClick={() => navigate(feature.properties.gallery_link)}
                                                >
                                                    {t('Thematic.GoToGallery', 'Ugrás a galériára')}
                                                </Button>
                                            )}
                                        </Box>
                                    </Popup>
                                </Marker>
                            );
                        })
                    )}
                </MapContainer>
            </Box>

            {/* Info Block (1/4 desktop, auto mobile) */}
            <Box 
                id="thematic-info-panel"
                sx={{ 
                    flex: 1, 
                    bgcolor: '#f5f5f5', 
                    borderLeft: isMobile ? 'none' : '1px solid #ddd', 
                    borderTop: isMobile ? '1px solid #ddd' : 'none',
                    overflowY: 'auto',
                    p: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: isMobile ? '40vh' : 'auto'
                }}
            >
                {/* Map Title */}
                <Typography variant="h6" sx={{ color: '#105b79', fontWeight: 'bold', mb: 2, textAlign: 'center', borderBottom: '2px solid #105b79', pb: 1 }}>
                    {type === 'routes' ? t('Thematic.TouristRoutes') : 
                     category?.toLowerCase() === 'nepviselet' ? t('Thematic.FolkCostume') : 
                     category?.toLowerCase() === 'nepi_epiteszet' ? t('Thematic.FolkArchitecture') : 
                     category?.toLowerCase() === 'szekelykapu' ? t('Thematic.Szekelykapu') : 
                     category?.toLowerCase() === 'panteon' ? t('Thematic.Pantheon') : 
                     category?.toLowerCase() === 'futuristique' ? t('Thematic.Futuristique') : 
                     category?.toLowerCase() === 'ballada' ? t('Thematic.Ballada') : 
                     category?.toLowerCase() === 'nepdal' ? t('Thematic.Nepdal') : 
                     category?.toLowerCase() === 'locsoloversek' ? t('Thematic.Locsoloversek') : 
                     category?.toLowerCase() === 'foldtani' ? t('Thematic.FoldtaniFeltarasok', 'Földtani feltárások') : t('Header.ThematicMaps')}
                </Typography>

                {/* Running Route Selector - Visible when Running checkbox is checked */}
                {showRunning && Array.isArray(runningData) && runningData.length > 0 && (
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                            {t('Map.Running.SelectRoute', 'Válasszon futóútvonalat')}:
                        </Typography>
                        <Select
                            fullWidth
                            size="small"
                            value={selectedInfo?.type === 'running_route' ? (String(selectedInfo.id || selectedInfo.name || '')) : ''}
                            onChange={(e) => {
                                const val = e.target.value;
                                const route = runningData.find(r => 
                                    String(r.id || r.properties?.id) === String(val) || 
                                    String(r.properties?.name) === String(val)
                                );
                                if (route) {
                                    const finalId = route.id || (route.properties && route.properties.id);
                                    const finalName = route.properties && route.properties.name;
                                    setSelectedInfo({ 
                                        ...(route.properties || {}), 
                                        id: finalId, 
                                        name: finalName,
                                        type: 'running_route' 
                                    });
                                    // Optionally focus on the selected route
                                    if (route.geometry && (route.geometry.type === 'MultiLineString' || route.geometry.type === 'LineString')) {
                                        const coords = route.geometry.type === 'MultiLineString' 
                                            ? route.geometry.coordinates.flat() 
                                            : route.geometry.coordinates;
                                        const positions = coords.map(coord => [coord[1], coord[0]]);
                                        const bounds = L.latLngBounds(positions);
                                        setFocusTarget({ bounds, coords: null });
                                    }
                                }
                            }}
                            displayEmpty
                        >
                            <MenuItem value="" disabled>
                                <em>{t('Map.Running.SelectPlaceholder', 'Válasszon...')}</em>
                            </MenuItem>
                            {runningData.map((route, idx) => {
                                const routeId = route.id || route.properties?.id;
                                const routeLabel = route.properties?.name || `Route ${routeId || idx}`;
                                const val = String(routeId || route.properties?.name || idx);
                                return (
                                    <MenuItem key={`select-run-${val}-${idx}`} value={val}>
                                        {routeLabel}
                                    </MenuItem>
                                );
                            })}
                        </Select>
                    </Box>
                )}

                {/* Hashtag Filter */}
                {allHashtags.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>{t('Thematic.FilterByHashtag')}</Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            <Chip 
                                label={t('Common.All')} 
                                size="small" 
                                onClick={() => setSelectedHashtag(null)}
                                color={selectedHashtag === null ? 'primary' : 'default'}
                                variant={selectedHashtag === null ? 'filled' : 'outlined'}
                                sx={{ cursor: 'pointer' }}
                            />
                            {Array.isArray(allHashtags) && allHashtags.map(tag => (
                                <Chip 
                                    key={tag} 
                                    label={tag} 
                                    size="small" 
                                    onClick={() => setSelectedHashtag(tag === selectedHashtag ? null : tag)}
                                    color={selectedHashtag === tag ? 'primary' : 'default'}
                                    variant={selectedHashtag === tag ? 'filled' : 'outlined'}
                                    sx={{ cursor: 'pointer' }}
                                />
                            ))}
                        </Box>
                    </Box>
                )}

                {/* Add New Object Button */}
                {GlobalState.userIsLogged && (
                    <Button 
                        variant="contained" 
                        fullWidth 
                        onClick={() => {
                            if (showRunning) {
                                navigate('/add-running-route');
                            } else if (type !== 'routes') {
                                navigate(`/add-thematic/${category}`);
                            }
                        }}
                        sx={{ mb: 2, bgcolor: '#105b79', '&:hover': { bgcolor: '#0d4a63' }, display: (type === 'routes' && !showRunning) ? 'none' : 'block' }}
                    >
                        {t('Thematic.AddNew')} +
                    </Button>
                )}

                {selectedInfo ? (
                    <Box>
                        <Typography variant="h5" sx={{ color: '#d32f2f', fontWeight: 'bold', mb: 2 }}>
                            {selectedInfo.nev || selectedInfo.name}
                        </Typography>
                    
                        {selectedInfo.type === 'tourist_route' && (
                            <>
                                <Typography variant="body1" sx={{ mb: 2 }}>{selectedInfo.leiras}</Typography>
                                <Box sx={{ mt: 2, p: 2, bgcolor: 'white', borderRadius: 1, boxShadow: 1 }}>
                                    <Typography variant="subtitle2" color="textSecondary">{t('Common.Details')}:</Typography>
                                    {selectedInfo.hossza && <Typography variant="body2">{t('Common.Length')}: {selectedInfo.hossza} km</Typography>}
                                    {selectedInfo.nehezseg && <Typography variant="body2">{t('Thematic.Difficulty')}: {selectedInfo.nehezseg}</Typography>}
                                    {selectedInfo.telep && <Typography variant="body2">{t('Common.Location')}: {selectedInfo.telep}</Typography>}
                                    {selectedInfo.reszletes && (
                                        <Box sx={{ mt: 1 }}>
                                            <Typography variant="subtitle2" color="textSecondary">{t('Thematic.DetailedDescription')}:</Typography>
                                            <Typography variant="body2">{selectedInfo.reszletes}</Typography>
                                        </Box>
                                    )}
                                </Box>
                            </>
                        )}

                        {selectedInfo.type === 'marked_path' && (
                            <>
                                <Typography variant="body1" sx={{ mb: 2 }}>{t('Thematic.MarkedTouristRoute')}</Typography>
                                <Box sx={{ mt: 2, p: 2, bgcolor: 'white', borderRadius: 1, boxShadow: 1 }}>
                                    <Typography variant="subtitle2" color="textSecondary">{t('Common.Details')}:</Typography>
                                    <Typography variant="body2">{t('Events.Category')}: {selectedInfo.szimbolum}</Typography>
                                    {selectedInfo.hossza && <Typography variant="body2">{t('Events.Length')}: {selectedInfo.hossza} km</Typography>}
                                </Box>
                                {markIcons[selectedInfo.szimbolum?.toLowerCase().trim()] && (
                                    <Box sx={{ mt: 2, textAlign: 'center' }}>
                                        <img 
                                            src={markIcons[selectedInfo.szimbolum.toLowerCase().trim()]} 
                                            alt={selectedInfo.szimbolum}
                                            style={{ width: '72px', height: '72px' }}
                                        />
                                    </Box>
                                )}
                            </>
                        )}

                        {selectedInfo.type === 'running_route' && (
                            <>
                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>{selectedInfo.description}</Typography>
                                </Box>
                                <Box sx={{ mt: 2, p: 2, bgcolor: 'white', borderRadius: 1, boxShadow: 1 }}>
                                    <Typography variant="subtitle2" color="textSecondary">{t('Common.Details')}:</Typography>
                                    <Typography variant="body2">{t('Events.Category')}: {selectedInfo.category}</Typography>
                                    {selectedInfo.distance && <Typography variant="body2">{t('Common.Length')}: {selectedInfo.distance} km</Typography>}
                                </Box>

                                <Drawer
                                    anchor={isMobile ? "bottom" : "right"}
                                    open={openTracker}
                                    onClose={() => setOpenTracker(false)}
                                    variant="persistent"
                                    PaperProps={{
                                        sx: {
                                            width: isMobile ? '100%' : 400,
                                            height: isMobile ? 'auto' : '100%',
                                            maxHeight: isMobile ? '40vh' : '100%',
                                            bgcolor: isMobile ? 'background.paper' : '#f5f5f5',
                                            borderRadius: isMobile ? '20px 20px 0 0' : 0,
                                            boxShadow: 3,
                                            pointerEvents: 'auto',
                                            visibility: openTracker ? 'visible' : 'hidden'
                                        }
                                    }}
                                    sx={{
                                        pointerEvents: 'none',
                                        '& .MuiDrawer-root': {
                                            pointerEvents: 'none'
                                        },
                                        '& .MuiBackdrop-root': {
                                            display: 'none'
                                        }
                                    }}
                                >
                                    <Box sx={{ p: 2, pt: isMobile ? 3 : 8 }}>
                                        {isMobile && (
                                            <Box 
                                                sx={{ 
                                                    width: 40, 
                                                    height: 4, 
                                                    bgcolor: 'grey.300', 
                                                    borderRadius: 2, 
                                                    margin: '0 auto', 
                                                    mb: 2,
                                                    position: 'absolute',
                                                    top: 8,
                                                    left: 'calc(50% - 20px)'
                                                }} 
                                            />
                                        )}
                                        <RunningTracker 
                                            route={{
                                                geometry: runningData.find(r => String(r.id || r.properties?.id) === String(selectedInfo.id))?.geometry,
                                                properties: selectedInfo
                                            }} 
                                            userLocation={userLocation} 
                                            isMobile={isMobile}
                                            onStartTracking={() => setIsTracking(true)}
                                            onStopTracking={() => setIsTracking(false)}
                                        />
                                        <Button 
                                            fullWidth 
                                            variant="outlined" 
                                            onClick={() => setOpenTracker(false)}
                                            sx={{ mt: 2 }}
                                        >
                                            {t('Common.Close', 'Bezárás')}
                                        </Button>
                                    </Box>
                                </Drawer>
                            </>
                        )}

                        {selectedInfo.type === 'point' && (
                            <>
                                {selectedInfo.image && (
                                    <img 
                                        src={selectedInfo.image.startsWith('http') ? selectedInfo.image : `${config[environment].apiEndpoint}${selectedInfo.image.startsWith('/') ? selectedInfo.image.substring(1) : selectedInfo.image}`} 
                                        alt={selectedInfo.name} 
                                        style={{ width: '100%', height: 'auto', borderRadius: 4, marginBottom: 16, cursor: 'pointer' }}
                                        onClick={() => setSelectedImage(selectedInfo.image.startsWith('http') ? selectedInfo.image : `${config[environment].apiEndpoint}${selectedInfo.image.startsWith('/') ? selectedInfo.image.substring(1) : selectedInfo.image}`)}
                                    />
                                )}
                            
                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="subtitle2" color="textSecondary">
                                        {t('Common.Category')}: {category?.toLowerCase() === 'nepviselet' ? t('Thematic.FolkCostume') : 
                                                   category?.toLowerCase() === 'nepi_epiteszet' ? t('Thematic.FolkArchitecture') : 
                                                   category?.toLowerCase() === 'panteon' ? t('Thematic.Pantheon') : 
                                                   category?.toLowerCase() === 'szekelykapu' ? t('Thematic.Szekelykapu') :
                                                   category?.toLowerCase() === 'ballada' ? t('Thematic.Ballada') :
                                                   category?.toLowerCase() === 'nepdal' ? t('Thematic.Nepdal') :
                                                   category?.toLowerCase() === 'locsoloversek' ? t('Thematic.Locsoloversek') :
                                                   category?.toLowerCase() === 'foldtani' ? t('Thematic.FoldtaniFeltarasok', 'Földtani feltárások') :
                                                   t('Thematic.Futuristique')}
                                    </Typography>
                                </Box>

                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="subtitle2" color="textSecondary">
                                        {category?.toLowerCase() === 'ballada' ? t('Thematic.BalladText') :
                                         category?.toLowerCase() === 'nepdal' ? t('Thematic.FolkSongText') :
                                         category?.toLowerCase() === 'locsoloversek' ? t('Thematic.EasterVerseText') :
                                         t('Common.Description')}:
                                    </Typography>
                                </Box>

                                <Typography variant="body1" sx={{ mb: 2, whiteSpace: (category?.toLowerCase() === 'ballada' || category?.toLowerCase() === 'nepdal' || category?.toLowerCase() === 'locsoloversek') ? 'pre-wrap' : 'normal' }}>
                                    {selectedInfo.description}
                                </Typography>
                            
                                {selectedInfo.hashtags && (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                                        {selectedInfo.hashtags.split(/[\s,]+/).filter(t => t.startsWith('#')).map(tag => (
                                            <Chip 
                                                key={tag} 
                                                label={tag} 
                                                size="small" 
                                                onClick={() => setSelectedHashtag(tag === selectedHashtag ? null : tag)}
                                                color={selectedHashtag === tag ? 'primary' : 'default'}
                                                variant={selectedHashtag === tag ? 'filled' : 'outlined'}
                                                sx={{ cursor: 'pointer' }}
                                            />
                                        ))}
                                    </Box>
                                )}

                                <Box sx={{ mt: 2, mb: 2, p: 2, bgcolor: 'white', borderRadius: 1, boxShadow: 1 }}>
                                    {/* Location details */}
                                    {selectedInfo.szek && (
                                        <Typography variant="body2">
                                            <strong>{t('Map.Toolbox.szek')}:</strong> {selectedInfo.szek}
                                        </Typography>
                                    )}
                                    {selectedInfo.kozsegvaros && (
                                        <Typography variant="body2">
                                            <strong>{t('Map.Toolbox.kozseg')}:</strong> {selectedInfo.kozsegvaros}
                                        </Typography>
                                    )}
                                    {selectedInfo.telepules_name || selectedInfo.telepules ? (
                                        <Typography variant="body2">
                                            <strong>{t('Map.Toolbox.telepules')}:</strong> {selectedInfo.telepules_name || selectedInfo.telepules}
                                        </Typography>
                                    ) : null}

                                    {/* Category specific details */}
                                    {category === 'panteon' && (
                                        <>
                                            {selectedInfo.birth && <Typography variant="body2" sx={{ mt: 0.5 }}><strong>{t('Thematic.Birth')}:</strong> {selectedInfo.birth}</Typography>}
                                            {selectedInfo.death && <Typography variant="body2"><strong>{t('Thematic.Death')}:</strong> {selectedInfo.death}</Typography>}
                                            {selectedInfo.works && (
                                                <Box sx={{ mt: 1 }}>
                                                    <Typography variant="subtitle2" color="textSecondary">{t('Thematic.Works')}:</Typography>
                                                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{selectedInfo.works}</Typography>
                                                </Box>
                                            )}
                                        </>
                                    )}

                                    {/* Folk Architecture specific details */}
                                    {category === 'nepi_epiteszet' && (
                                        <Box sx={{ mt: 1 }}>
                                            {selectedInfo.address && <Typography variant="body2"><strong>{t('Common.Address')}:</strong> {selectedInfo.address}</Typography>}
                                            {selectedInfo.built_year && <Typography variant="body2"><strong>{t('Thematic.BuiltYear')}:</strong> {selectedInfo.built_year}</Typography>}
                                            {selectedInfo.collected_by && <Typography variant="body2"><strong>{t('Thematic.CollectedBy')}:</strong> {selectedInfo.collected_by}</Typography>}
                                            {selectedInfo.sketch && <Typography variant="body2"><strong>{t('Thematic.Sketch')}:</strong> {selectedInfo.sketch}</Typography>}
                                            {selectedInfo.wall && <Typography variant="body2"><strong>{t('Thematic.Wall')}:</strong> {selectedInfo.wall}</Typography>}
                                            {selectedInfo.floor && <Typography variant="body2"><strong>{t('Thematic.Floor')}:</strong> {selectedInfo.floor}</Typography>}
                                            {selectedInfo.roof && <Typography variant="body2"><strong>{t('Thematic.Roof')}:</strong> {selectedInfo.roof}</Typography>}
                                            {selectedInfo.economic_buildings && (
                                                <Box sx={{ mt: 1 }}>
                                                    <Typography variant="subtitle2" color="textSecondary">{t('Thematic.EconomicBuildings')}:</Typography>
                                                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{selectedInfo.economic_buildings}</Typography>
                                                </Box>
                                            )}
                                            {selectedInfo.other_stamps && (
                                                <Box sx={{ mt: 1 }}>
                                                    <Typography variant="subtitle2" color="textSecondary">{t('Thematic.OtherStamps')}:</Typography>
                                                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{selectedInfo.other_stamps}</Typography>
                                                </Box>
                                            )}
                                        </Box>
                                    )}

                                    {/* Székelykapu specific details */}
                                    {category === 'szekelykapu' && (
                                        <Box sx={{ mt: 1 }}>
                                            {selectedInfo.erected_by && <Typography variant="body2"><strong>{t('Thematic.ErectedBy')}:</strong> {selectedInfo.erected_by}</Typography>}
                                            {selectedInfo.erection_year && <Typography variant="body2"><strong>{t('Thematic.ErectionYear')}:</strong> {selectedInfo.erection_year}</Typography>}
                                            {selectedInfo.wood_type && <Typography variant="body2"><strong>{t('Thematic.WoodType')}:</strong> {selectedInfo.wood_type}</Typography>}
                                            {selectedInfo.is_painted !== undefined && <Typography variant="body2"><strong>{t('Thematic.IsPainted')}:</strong> {selectedInfo.is_painted ? t('Common.Yes') : t('Common.No')}</Typography>}
                                            {selectedInfo.gate_inscription && (
                                                <Box sx={{ mt: 1 }}>
                                                    <Typography variant="subtitle2" color="textSecondary">{t('Thematic.GateInscription')}:</Typography>
                                                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{selectedInfo.gate_inscription}</Typography>
                                                </Box>
                                            )}
                                        </Box>
                                    )}

                                    {/* Folk Costume specific details */}
                                    {category === 'nepviselet' && (
                                        <Box sx={{ mt: 1 }}>
                                            {selectedInfo.collection_year && <Typography variant="body2"><strong>{t('Thematic.CollectionYear')}:</strong> {selectedInfo.collection_year}</Typography>}
                                            {selectedInfo.collected_by && <Typography variant="body2"><strong>{t('Thematic.CollectedBy')}:</strong> {selectedInfo.collected_by}</Typography>}
                                            {selectedInfo.drawn_by && <Typography variant="body2"><strong>{t('Thematic.DrawnBy')}:</strong> {selectedInfo.drawn_by}</Typography>}
                                            {selectedInfo.costume_type && <Typography variant="body2"><strong>{t('Thematic.CostumeType')}:</strong> {selectedInfo.costume_type}</Typography>}
                                            {(selectedInfo.is_child || selectedInfo.is_elderly) && (
                                                <Typography variant="body2">
                                                    <strong>{t('Common.Category')}:</strong> { [selectedInfo.is_child && t('Thematic.IsChild'), selectedInfo.is_elderly && t('Thematic.IsElderly')].filter(Boolean).join(', ') }
                                                </Typography>
                                            )}
                                            {selectedInfo.period && <Typography variant="body2"><strong>{t('Thematic.Period')}:</strong> {selectedInfo.period}</Typography>}
                                            {selectedInfo.special_notes && (
                                                <Box sx={{ mt: 1 }}>
                                                    <Typography variant="subtitle2" color="textSecondary">{t('Thematic.SpecialNotes')}:</Typography>
                                                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{selectedInfo.special_notes}</Typography>
                                                </Box>
                                            )}
                                        </Box>
                                    )}

                                    {/* Foldtani specific details */}
                                    {category === 'foldtani' && (
                                        <Box sx={{ mt: 1 }}>
                                            {selectedInfo.stratigraphic_age && <Typography variant="body2"><strong>{t('Thematic.StratigraphicAge', 'Rétegtani kor')}:</strong> {selectedInfo.stratigraphic_age}</Typography>}
                                            {selectedInfo.stratigraphic_formation && <Typography variant="body2"><strong>{t('Thematic.StratigraphicFormation', 'Rétegtani formáció')}:</strong> {selectedInfo.stratigraphic_formation}</Typography>}
                                            {selectedInfo.rock_type && <Typography variant="body2"><strong>{t('Thematic.RockType', 'Kőzettípus')}:</strong> {selectedInfo.rock_type}</Typography>}
                                            {selectedInfo.characteristic_mineral && <Typography variant="body2"><strong>{t('Thematic.CharacteristicMineral', 'Jellegzetes ásvány')}:</strong> {selectedInfo.characteristic_mineral}</Typography>}
                                            {selectedInfo.characteristic_fossil && <Typography variant="body2"><strong>{t('Thematic.CharacteristicFossil', 'Jellegzetes kövület')}:</strong> {selectedInfo.characteristic_fossil}</Typography>}
                                        </Box>
                                    )}

                                    {/* Ballad / Folk Song / Verse specific details */}
                                   {(category?.toLowerCase() === 'ballada' || category?.toLowerCase() === 'nepdal' || category?.toLowerCase() === 'locsoloversek') && (
                                       <Box sx={{ mt: 1 }}>
                                            {selectedInfo.motif_marker && <Typography variant="body2" sx={{ mt: 0.5 }}><strong>{t('Thematic.MotifMarker')}:</strong> {selectedInfo.motif_marker}</Typography>}
                                            {selectedInfo.informant && <Typography variant="body2"><strong>{t('Thematic.Informant')}:</strong> {selectedInfo.informant}</Typography>}
                                            {selectedInfo.collected_by && <Typography variant="body2"><strong>{t('Thematic.CollectedBy')}:</strong> {selectedInfo.collected_by}</Typography>}
                                            {selectedInfo.first_publication && <Typography variant="body2"><strong>{t('Thematic.FirstPublication')}:</strong> {selectedInfo.first_publication}</Typography>}
                                            {selectedInfo.selected_edition && (
                                                <Box sx={{ mt: 1 }}>
                                                    <Typography variant="subtitle2" color="textSecondary">{t('Thematic.SelectedEdition')}:</Typography>
                                                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{selectedInfo.selected_edition}</Typography>
                                                </Box>
                                            )}
                                        </Box>
                                    )}
                                </Box>

                                {GlobalState.userIsLogged && (
                                    <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                                        <Button
                                            variant="contained"
                                            fullWidth
                                            onClick={() => {
                                                const editId = (selectedInfo.id) || selectedInfo.pk;
                                                navigate(`/edit-thematic/${category}/${editId}`);
                                            }}
                                            sx={{ bgcolor: '#105b79' }}
                                        >
                                            {t('Common.Edit')}
                                        </Button>
                                    </Box>
                                )}
                            
                                {category === 'foldtani' && !selectedInfo && (
                                    <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
                                        <strong>{t('Thematic.FoldtaniFeltarasok')}</strong><br />
                                        {t('Thematic.SelectFoldtaniPrompt')}
                                    </Alert>
                                )}

                                {category === 'nepviselet' && (
                                    <Alert severity="info" sx={{ mb: 2 }}>
                                        {t('Thematic.FolkCostumeAlert')}
                                    </Alert>
                                )}
                            
                                {category === 'nepi_epiteszet' && (
                                    <Alert severity="info" sx={{ mb: 2 }}>
                                        {t('Thematic.FolkArchitectureAlert')}
                                    </Alert>
                                )}

                                {category === 'panteon' && (
                                    <Alert severity="info" sx={{ mb: 2 }}>
                                        {t('Thematic.PantheonAlert')}
                                    </Alert>
                                )}
                            
                                {category === 'futuristique' && (
                                    <Alert severity="info" sx={{ mb: 2 }}>
                                        {t('Thematic.FuturistiqueAlert')}
                                    </Alert>
                                )}

                                {selectedInfo.gallery_link && (
                                    <Button 
                                        variant="contained" 
                                        fullWidth 
                                        onClick={() => navigate(selectedInfo.gallery_link)}
                                        sx={{ mt: 'auto', bgcolor: '#d32f2f' }}
                                    >
                                        {t('Thematic.GoToGallery')}
                                    </Button>
                                )}
                            </>
                        )}

                        <Button 
                            variant="outlined" 
                            fullWidth 
                            onClick={() => setSelectedInfo(null)}
                            sx={{ mt: 4 }}
                        >
                            {t('Common.Back')}
                        </Button>
                    </Box>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', minHeight: isMobile ? '200px' : 'auto', color: 'textSecondary', textAlign: 'center' }}>
                        <Typography variant="body1">
                            {getSelectPrompt()}
                        </Typography>
                    </Box>
                )}
            </Box>

            {/* Modal for Large Image */}
            <Modal
                open={!!selectedImage}
                onClose={() => setSelectedImage(null)}
                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
                <Box sx={{ maxWidth: '95%', maxHeight: '95%', bgcolor: 'background.paper', p: 1, boxShadow: 24, borderRadius: 1, overflow: 'hidden' }}>
                    <img 
                        src={selectedImage ? (selectedImage.startsWith('http') ? selectedImage : `${config[environment].apiEndpoint}${selectedImage.startsWith('/') ? selectedImage.substring(1) : selectedImage}`) : ''} 
                        alt="Large" 
                        style={{ width: '100%', height: 'auto', maxHeight: '85vh', objectFit: 'contain', display: 'block' }} 
                    />
                    <Button onClick={() => setSelectedImage(null)} fullWidth sx={{ mt: 1 }}>{t('Common.Close')}</Button>
                </Box>
            </Modal>
        </Box>
    );
};

export default ThematicMap;
